import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2, Folder, Package, ChevronRight, ArrowLeft, Search, X, Plus, Minus, Check } from 'lucide-react';
import { mediaUrl } from '../utils/media';
import { uomLabel } from '../utils/uom';
import { curLabel } from '../utils/currency';
import { useT } from '../utils/translate';
import { setLine } from '../store/cartSlice';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const UNCAT = '__uncat__';

function fmtPrice(price, t) {
  if (!price) return t('catalog.noPrice');
  const amount = Number(price.amount || 0).toLocaleString('en-US');
  const unitLabel = price.unit ? uomLabel(price.unit, t) : '';
  const cur = curLabel(price.currency);
  return unitLabel ? `${amount} ${cur} / ${unitLabel}` : `${amount} ${cur}`;
}

// Bitta mahsulotni bir nechta birlikda (dona/pachka/karobka) BIR VAQTDA
// savatga qo'shish — SalesAgentPage.jsx'dagi AddToCartModal bilan bir xil
// g'oya (soddalashtirilgan: ostatka cheklovi yo'q, mijoz portalida ostatka
// umuman ko'rsatilmaydi).
function UnitPickerModal({ product, onClose, t }) {
  const dispatch = useDispatch();
  const lines = useSelector(s => s.cart.items.filter(i => i.productId === product._id));
  const [qtys, setQtys] = useState(() => {
    const m = {};
    lines.forEach(l => { m[l.unitKey] = l.qty; });
    return m;
  });

  const setQty = (u, v) => {
    const n = Math.max(0, Math.min(99999, Math.floor(Number(v) || 0)));
    setQtys(prev => ({ ...prev, [u.key]: n }));
  };
  const inc = (u) => setQty(u, (qtys[u.key] || 0) + 1);
  const dec = (u) => setQty(u, (qtys[u.key] || 0) - 1);

  const total = product.units.reduce((s, u) => s + (qtys[u.key] || 0) * u.amount, 0);
  const totalQty = product.units.reduce((s, u) => s + (qtys[u.key] || 0), 0);
  const currency = product.units[0]?.currency;

  const confirm = () => {
    product.units.forEach(u => {
      dispatch(setLine({
        productId: product._id, unitKey: u.key, unitLabel: uomLabel(u.uom, t),
        name: product.name, image: product.images?.[0] || null,
        price: u.amount, currency: u.currency, qty: qtys[u.key] || 0,
      }));
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-modal w-full sm:max-w-sm max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-100 shrink-0">
          <h3 className="font-semibold text-ink text-sm truncate pr-2">{product.name}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-ink-tertiary hover:bg-surface-100 hover:text-ink transition-colors shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {product.units.map(u => {
            const q = qtys[u.key] || 0;
            const label = uomLabel(u.uom, t);
            return (
              <div key={u.key} className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border transition-colors ${q > 0 ? 'border-primary-300 bg-primary-50/40' : 'border-surface-200'}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{label}</p>
                  <p className="text-[11px] text-ink-tertiary">{u.amount.toLocaleString('en-US')} {curLabel(u.currency)}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button type="button" onClick={() => dec(u)}
                    className="w-8 h-8 rounded-lg bg-surface-100 hover:bg-surface-200 flex items-center justify-center transition-colors">
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="w-10 text-center text-base font-semibold">{q}</span>
                  <button type="button" onClick={() => inc(u)}
                    className="w-8 h-8 rounded-lg bg-primary-600 hover:bg-primary-700 text-white flex items-center justify-center transition-colors">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="shrink-0 border-t border-surface-100 px-4 py-3 space-y-3 safe-bottom [--safe-pad:0.75rem]">
          <div className="flex items-center justify-between">
            <span className="text-sm text-ink-secondary">{t('cart.total')}</span>
            <span className="text-lg font-bold text-ink">{total.toLocaleString('en-US')} {curLabel(currency)}</span>
          </div>
          <button type="button" onClick={confirm}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-primary-600 hover:bg-primary-700 text-white font-semibold text-sm transition-colors active:scale-[0.98] disabled:opacity-50">
            <Check className="w-4 h-4" /> {t('catalog.done')}
          </button>
          {totalQty === 0 && lines.length > 0 && (
            <p className="text-[11px] text-center text-ink-tertiary">{t('catalog.doneRemovesAll')}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductCard({ p, t, ordersEnabled }) {
  const [imgError, setImgError] = useState(false);
  const [picking, setPicking] = useState(false);
  const dispatch = useDispatch();
  const cartQty = useSelector(s => s.cart.items.filter(i => i.productId === p._id).reduce((n, i) => n + i.qty, 0));
  const singleUnit = p.units?.length === 1 ? p.units[0] : null;
  const canOrder = ordersEnabled && !!p.units?.length;

  const addSingle = () => dispatch(setLine({
    productId: p._id, unitKey: singleUnit.key, unitLabel: uomLabel(singleUnit.uom, t),
    name: p.name, image: p.images?.[0] || null,
    price: singleUnit.amount, currency: singleUnit.currency, qty: cartQty + 1,
  }));
  const decSingle = () => dispatch(setLine({
    productId: p._id, unitKey: singleUnit.key, unitLabel: uomLabel(singleUnit.uom, t),
    name: p.name, image: p.images?.[0] || null,
    price: singleUnit.amount, currency: singleUnit.currency, qty: cartQty - 1,
  }));

  return (
    <div className="rounded-xl border border-surface-200 bg-white overflow-hidden flex flex-col shadow-sm">
      <div className="w-full aspect-square bg-surface-100 flex items-center justify-center overflow-hidden shrink-0">
        {p.images?.[0] && !imgError
          ? <img src={mediaUrl(p.images[0])} alt="" className="w-full h-full object-cover" onError={() => setImgError(true)} />
          : <Package className="w-6 h-6 text-ink-tertiary" />}
      </div>
      <div className="flex-1 flex flex-col p-2 gap-1">
        <p className="text-xs font-medium text-ink leading-tight line-clamp-2 min-h-[2rem]">{p.name}</p>
        <p className={`text-sm font-bold leading-none ${p.price ? 'text-primary-700' : 'text-ink-tertiary font-normal'}`}>
          {fmtPrice(p.price, t)}
        </p>
        {canOrder && singleUnit && (
          cartQty > 0 ? (
            <div className="flex items-center justify-between mt-1 rounded-lg bg-primary-50 px-1 py-1">
              <button onClick={decSingle} className="w-6 h-6 flex items-center justify-center rounded-md bg-white text-primary-700 shadow-sm">
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs font-semibold text-primary-700">{cartQty}</span>
              <button onClick={addSingle} className="w-6 h-6 flex items-center justify-center rounded-md bg-white text-primary-700 shadow-sm">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button onClick={addSingle} className="mt-1 flex items-center justify-center gap-1 rounded-lg bg-primary-600 text-white text-xs font-medium py-1.5">
              <Plus className="w-3.5 h-3.5" /> {t('catalog.addToCart')}
            </button>
          )
        )}
        {canOrder && !singleUnit && (
          <button onClick={() => setPicking(true)}
            className={`mt-1 flex items-center justify-center gap-1 rounded-lg text-xs font-medium py-1.5 ${
              cartQty > 0 ? 'bg-primary-50 text-primary-700' : 'bg-primary-600 text-white'
            }`}>
            {cartQty > 0 ? `${cartQty} ${t('catalog.selected')}` : (<><Plus className="w-3.5 h-3.5" /> {t('catalog.chooseUnit')}</>)}
          </button>
        )}
      </div>
      {picking && <UnitPickerModal product={p} onClose={() => setPicking(false)} t={t} />}
    </div>
  );
}

// Mijoz-portal katalogi — asosan KO'RISH uchun; agar tashkilotda
// clientOrdersEnabled yoqilgan bo'lsa, narxi bor mahsulotlarga savatga
// qo'shish tugmasi ham chiqadi (CartPage'da buyurtma sifatida yuboriladi).
export default function CatalogPage() {
  const t = useT();
  const customer = useSelector(s => s.auth.customer);
  const ordersEnabled = !!customer?.organization?.ordersEnabled;
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [path, setPath] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/client/catalog/categories`).catch(() => ({ data: {} })),
      axios.get(`${API}/client/catalog/products`).catch(() => ({ data: {} })),
    ]).then(([catRes, prodRes]) => {
      setCategories(catRes.data.categories || []);
      setProducts(prodRes.data.products || []);
    }).finally(() => setLoading(false));
  }, []);

  const childCats = useMemo(() => {
    const m = {};
    categories.forEach(c => {
      const pid = c.parent?._id ?? c.parent ?? null;
      const key = pid ? String(pid) : 'root';
      (m[key] ||= []).push(c);
    });
    Object.values(m).forEach(arr => arr.sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || a.name.localeCompare(b.name)));
    return m;
  }, [categories]);

  const prodByCat = useMemo(() => {
    const m = {};
    products.forEach(p => {
      const cid = p.category?._id ?? p.category ?? null;
      const key = cid ? String(cid) : UNCAT;
      (m[key] ||= []).push(p);
    });
    Object.values(m).forEach(arr => arr.sort((a, b) => a.name.localeCompare(b.name)));
    return m;
  }, [products]);

  const currentId = path.length ? String(path[path.length - 1]._id) : 'root';
  const folders = childCats[currentId] || [];
  const directProducts = path.length ? (prodByCat[currentId] || []) : (prodByCat[UNCAT] || []);

  const searchHits = search.trim()
    ? products.filter(p => p.name.toLowerCase().includes(search.trim().toLowerCase()))
    : null;

  const enter = (cat) => setPath(prev => [...prev, cat]);
  const goUp = () => setPath(prev => prev.slice(0, -1));
  const goRoot = () => setPath([]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-7 h-7 animate-spin text-ink-tertiary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 p-3 space-y-2 border-b border-surface-100 bg-white">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-tertiary" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t('catalog.searchPlaceholder')}
            className="input input-with-icon pr-9" />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-tertiary">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {!search && path.length > 0 && (
          <div className="flex items-center gap-1 text-xs text-ink-tertiary overflow-x-auto">
            <button onClick={goUp} className="p-1 rounded-lg hover:bg-surface-100 text-ink-secondary shrink-0">
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>
            <button onClick={goRoot} className="shrink-0 hover:text-ink">{t('catalog.allFolders')}</button>
            {path.map((c, i) => (
              <React.Fragment key={c._id}>
                <ChevronRight className="w-3 h-3 shrink-0" />
                <button onClick={() => setPath(path.slice(0, i + 1))} className="shrink-0 truncate max-w-[120px] hover:text-ink">
                  {c.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {search ? (
          searchHits.length === 0 ? (
            <p className="text-center text-sm text-ink-tertiary py-10">{t('catalog.notFound')}</p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {searchHits.map(p => <ProductCard key={p._id} p={p} t={t} ordersEnabled={ordersEnabled} />)}
            </div>
          )
        ) : (
          <>
            {folders.length === 0 && directProducts.length === 0 && (
              <p className="text-center text-sm text-ink-tertiary py-10">{t('catalog.empty')}</p>
            )}
            {folders.length > 0 && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                {folders.map(c => (
                  <button key={c._id} onClick={() => enter(c)}
                    className="rounded-xl border border-surface-200 bg-white overflow-hidden flex flex-col text-left shadow-sm">
                    <div className="w-full aspect-[2/1] bg-surface-100 flex items-center justify-center overflow-hidden shrink-0">
                      {c.image
                        ? <img src={mediaUrl(c.image)} alt="" className="w-full h-full object-cover" />
                        : <Folder className="w-6 h-6" style={c.color ? { color: c.color } : undefined} />}
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-medium text-ink leading-tight line-clamp-2">{c.name}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {directProducts.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {directProducts.map(p => <ProductCard key={p._id} p={p} t={t} ordersEnabled={ordersEnabled} />)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
